import React, { useEffect, useState, useContext } from 'react';
import { API, Storage } from 'aws-amplify';
import operationData from '../../../../../Operation/data.json';
import { Context } from '../../../../Context';
import FoodDeliveryTabs from './FoodDeliveryTabs';

const API_NAME = 'foodDelivery';
const INDEX = '0';

const MarketingPoster = () => {
  const { institutionData } = useContext(Context);
  const institution = operationData.foodDeliveryInstitution;
  const activeInstitution = institution || institutionData?.institution || '';

  const [posters, setPosters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [error, setError] = useState('');

  const fetchPosters = async () => {
    if (!activeInstitution) return;
    setLoading(true);
    try {
      const path = `/admin/settings/${activeInstitution}/${INDEX}`;
      const res = await API.get(API_NAME, path, { queryStringParameters: { type: 'poster' } });
      const data = res?.data || res || [];
      setPosters(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.message || 'Failed to load posters');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchPosters(); }, [activeInstitution]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) setSelectedFile(file);
  };

  const uploadToS3 = async (file) => {
    setIsUploading(true);
    try {
      const ext = (file.name || 'jpg').split('.').pop();
      const fileName = `posters/${activeInstitution}/${Date.now()}.${ext}`;
      const result = await Storage.put(fileName, file, { contentType: file.type || 'image/jpeg', level: 'public' });
      const presigned = await Storage.get(result.key, { level: 'public' });
      const cleanUrl = presigned.split('?')[0];
      return cleanUrl;
    } catch (err) {
      setError(err?.message || 'Upload failed');
      return null;
    } finally { setIsUploading(false); }
  };

  const handleAddPoster = async () => {
    if (!selectedFile) return setError('Select an image first');
    setLoading(true);
    try {
      const url = await uploadToS3(selectedFile);
      if (!url) throw new Error('Upload failed');
      const updated = [
        ...posters,
        { imageUrl: url, active: true, order: posters.length, id: Date.now().toString() },
      ];
      const path = `/admin/settings/${activeInstitution}/${INDEX}`;
      await API.post(API_NAME, path, { queryStringParameters: { type: 'poster' }, body: updated });
      setSelectedFile(null);
      await fetchPosters();
    } catch (err) {
      setError(err?.message || 'Failed to add poster');
    } finally { setLoading(false); }
  };

  const handleDelete = async (posterId) => {
    if (posters.length <= 1) return setError('At least 1 poster must remain.');
    setLoading(true);
    try {
      const path = `/admin/settings/${activeInstitution}/${INDEX}`;
      await API.del(API_NAME, path, { queryStringParameters: { type: 'poster' }, body: { posterId } });
      await fetchPosters();
    } catch (err) {
      setError(err?.message || 'Failed to delete poster');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen w-full bg-gray-50">
      <FoodDeliveryTabs />
      <div className="px-4 py-6 lg:px-8">
        <div className="mx-auto max-w-5xl space-y-6">
          {/* Header */}
          <div className="bg-white px-6 py-6 rounded-lg shadow-sm border border-gray-200">
            <h1 className="text-2xl font-extrabold text-[#1F2937]">Food Delivery — Marketing Posters</h1>
            <p className="mt-1 text-sm text-gray-500">Manage promotional images shown on your food delivery listing • {activeInstitution}</p>
          </div>

          {/* Messages */}
          {error && (
            <div className="px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Upload Section */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Upload New Poster</h2>
            <div className="flex gap-3 flex-wrap">
              <label className="flex-1 min-w-64 cursor-pointer">
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 hover:bg-gray-50 transition">
                  <div className="text-3xl mb-2">🖼️</div>
                  <div className="text-sm font-semibold text-gray-900">Choose Image</div>
                  <div className="text-xs text-gray-500 mt-1">JPG, PNG (Max 5MB)</div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                    disabled={isUploading || loading}
                  />
                </div>
              </label>
              <div className="flex flex-col gap-2 justify-end">
                {selectedFile && (
                  <div className="text-sm text-gray-700 p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="font-semibold">📄 {selectedFile.name}</div>
                    <div className="text-xs text-gray-500">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</div>
                  </div>
                )}
                <button
                  onClick={handleAddPoster}
                  disabled={isUploading || loading || !selectedFile}
                  className="px-6 py-2.5 rounded-lg bg-gray-900 text-white font-semibold hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition whitespace-nowrap"
                >
                  {isUploading ? "⏳ Uploading..." : "📤 Upload & Add"}
                </button>
              </div>
            </div>
          </div>

          {/* Posters Grid */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Active Posters ({posters.length})</h2>
            {loading ? (
              <div className="text-center py-12 text-gray-500">Loading posters...</div>
            ) : posters.length === 0 ? (
              <div className="rounded-lg border border-dashed border-gray-300 px-4 py-12 text-center text-gray-500">
                No posters yet. Upload your first promotional image to get started.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {posters.map((poster, idx) => (
                  <div key={poster.id || idx} className="group relative rounded-lg overflow-hidden border border-gray-200 hover:border-gray-400 transition">
                    <img
                      src={poster.imageUrl}
                      alt={`Poster ${idx + 1}`}
                      className="w-full h-48 object-cover group-hover:brightness-75 transition"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition">
                      {posters.length > 1 && (
                        <button
                          onClick={() => handleDelete(poster.id)}
                          className="px-4 py-2 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700"
                        >
                          🗑️ Delete
                        </button>
                      )}
                    </div>
                    <div className="absolute top-2 right-2 px-2 py-1 bg-gray-900 text-white text-xs font-semibold rounded">
                      #{idx + 1}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h3 className="font-semibold text-blue-900 mb-2">ℹ️ Poster Guidelines</h3>
            <ul className="text-sm text-blue-800 space-y-2">
              <li>• Recommended size: 1080x1920 pixels (9:16 aspect ratio)</li>
              <li>• File format: JPG or PNG (Max 5MB per image)</li>
              <li>• Posters are displayed in order on the app</li>
              <li>• Keep at least one poster active at all times</li>
              <li>• Update posters regularly for better engagement</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketingPoster;
